/**
 * RESOURCE EDITOR CONSTANTS
 *
 * This file contains API endpoints and configuration constants
 * for the Resource Editor feature.
 *
 * @module ResourceEditor/Constants
 */

// ============================================
// API ENDPOINTS
// ============================================

/**
 * API endpoints for resource operations
 */
export const RESOURCE_API = {
  /** Create a new resource */
  CREATE: '/.netlify/functions/admin-resource-create',

  /** Update an existing resource */
  UPDATE: '/.netlify/functions/admin-resource-update',

  /** Fetch a single resource by ID */
  GET: (id: string) => `/.netlify/functions/admin-resource/${id}`,

  /** Fetch all resources */
  GET_ALL: '/.netlify/functions/admin-resources',

  /** Delete a resource */
  DELETE: '/.netlify/functions/admin-resource-delete',

  /** Bulk update resources */
  BULK_UPDATE: '/.netlify/functions/admin-resources-bulk-update',

  /** Bulk delete resources */
  BULK_DELETE: '/.netlify/functions/admin-resources-bulk-delete',
} as const;

// ============================================
// EDITOR MODE CONFIGURATION
// ============================================

export const RESOURCE_EDITOR_MODES = {
  LEGACY: 'legacy',
  RICH_READONLY: 'rich-readonly',
  RICH_WRITE: 'rich-write',
} as const;

export type ResourceEditorMode =
  (typeof RESOURCE_EDITOR_MODES)[keyof typeof RESOURCE_EDITOR_MODES];

export const RESOURCE_EDITOR_FORCE_LEGACY_KEY = 'resource_editor_force_legacy';

export const resolveResourceEditorMode = (
  value?: string | null
): ResourceEditorMode => {
  if (value === RESOURCE_EDITOR_MODES.RICH_READONLY) {
    return RESOURCE_EDITOR_MODES.RICH_READONLY;
  }

  if (value === RESOURCE_EDITOR_MODES.RICH_WRITE) {
    return RESOURCE_EDITOR_MODES.RICH_WRITE;
  }

  if (value === RESOURCE_EDITOR_MODES.LEGACY) {
    return RESOURCE_EDITOR_MODES.LEGACY;
  }

  return RESOURCE_EDITOR_MODES.LEGACY;
};

export const resolveResourceEditorModeWithOverride = (
  modeValue?: string | null,
  forceLegacy = false
): ResourceEditorMode => {
  if (forceLegacy) {
    return RESOURCE_EDITOR_MODES.LEGACY;
  }

  return resolveResourceEditorMode(modeValue);
};

export const isRichEditorReadOnlyMode = (
  mode: ResourceEditorMode
): boolean => {
  return mode === RESOURCE_EDITOR_MODES.RICH_READONLY;
};

export const isRichEditorChangeEnabled = (
  mode: ResourceEditorMode
): boolean => {
  return mode === RESOURCE_EDITOR_MODES.RICH_WRITE;
};

export const isRichEditorDisabled = (
  mode: ResourceEditorMode,
  isSubmitting: boolean
): boolean => {
  return isSubmitting || isRichEditorReadOnlyMode(mode);
};

// ============================================
// STATUS OPTIONS
// ============================================

/**
 * Resource status options for the editor
 */
export const RESOURCE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'archived', label: 'Archived' },
] as const;

// ============================================
// FILE UPLOAD CONFIGURATION
// ============================================

/**
 * File upload configuration
 */
export const RESOURCE_UPLOAD_MAX_SIZE_MB = 5;

export const FILE_UPLOAD_CONFIG = {
  /** Maximum file size in bytes (5MB) */
  MAX_SIZE: RESOURCE_UPLOAD_MAX_SIZE_MB * 1024 * 1024,

  /** Accepted file types */
  ACCEPTED_TYPES: [
    '.pdf',
    '.doc',
    '.docx',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.xlsx',
    '.pptx',
    '.txt',
  ],

  /** Maximum number of files */
  MAX_FILES: 1,
} as const;

// ============================================
// FORM DEFAULTS
// ============================================

/**
 * Default values for the resource form
 */
export const RESOURCE_FORM_DEFAULTS = {
  category: 'publication_position_statement',
  language: 'en',
  isFeatured: false,
  isPublic: true,
  status: 'draft',
  authors: [{ name: '', title: '', affiliation: '' }],
} as const;

// ============================================
// VALIDATION RULES
// ============================================

/**
 * Form validation rules
 */
export const VALIDATION_RULES = {
  title: {
    minLength: 3,
    maxLength: 200,
    required: true,
  },
  description: {
    minLength: 20,
    maxLength: 500,
    required: true,
  },
  authors: {
    min: 1,
    required: true,
  },
} as const;

// ============================================
// TOAST MESSAGES
// ============================================

/**
 * Toast notification messages
 */
export const TOAST_MESSAGES = {
  CREATE_SUCCESS: 'Resource created successfully!',
  UPDATE_SUCCESS: 'Resource updated successfully!',
  DELETE_SUCCESS: 'Resource deleted successfully!',
  CREATE_ERROR: 'Failed to create resource. Please try again.',
  UPDATE_ERROR: 'Failed to update resource. Please try again.',
  DELETE_ERROR: 'Failed to delete resource. Please try again.',
  VALIDATION_ERROR: 'Please fix the errors in the form.',
  AUTH_ERROR: 'You must be logged in to perform this action.',
} as const;